import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  IconButton,
  Divider,
  MenuItem,
  InputAdornment,
  Paper,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import BusinessIcon from "@mui/icons-material/Business";
import BadgeIcon from "@mui/icons-material/Badge";
import PaymentsIcon from "@mui/icons-material/Payments";
import ReceiptIcon from "@mui/icons-material/Receipt";

import Layout from "../../components/layout/Layout";
import PayslipPreviewModal from "./PayslipPreviewModal";
import { PayslipData, CustomField, SalaryItem } from "../../types/payslip";
import { formatINR, numberToIndianWords } from "../../utils/numberToWords";
import adminUsersApi from "../../api/adminUsers";
import globalSettingsApi from "../../api/globalSettings";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR - 2, CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1];

const DEFAULT_COMPANY = {
  companyName: "KINNOW DAILY FOODS PRIVATE LIMITED",
  addressLine1: "Khewat 2794, Khatoni 3546, Property Id: 3GTVC2H7 - (125033)",
  addressLine2: "",
  city: "Hisar",
  state: "Haryana",
  pincode: "125033",
  country: "India",
  logoUrl: "/images/kinnow.png",
  email: "support@kinnowdaily.com",
  phone: "+91 9876543210",
  gstin: "07AAACK1234F1Z5",
  pan: "AAACK1234F",
};

const DEFAULT_EARNINGS: SalaryItem[] = [
  { id: "e1", name: "Basic Salary", amount: 25000 },
  { id: "e2", name: "House Rent Allowance (HRA)", amount: 10000 },
  { id: "e3", name: "Conveyance Allowance", amount: 3000 },
  { id: "e4", name: "Special Allowance", amount: 5000 },
  { id: "e5", name: "Performance Bonus / Incentive", amount: 2000 },
];

const DEFAULT_DEDUCTIONS: SalaryItem[] = [
  { id: "d1", name: "Provident Fund (EPF)", amount: 1800 },
  { id: "d2", name: "Professional Tax (PT)", amount: 200 },
  { id: "d3", name: "Tax Deducted at Source (TDS)", amount: 1500 },
];

export const PayslipGenerator: React.FC = () => {
  const currentMonthName = MONTHS[new Date().getMonth()];

  const [payMonth, setPayMonth] = useState<string>(currentMonthName);
  const [payYear, setPayYear] = useState<number>(CURRENT_YEAR);

  const [company, setCompany] = useState(DEFAULT_COMPANY);

  const [employee, setEmployee] = useState({
    name: "",
    employeeId: "",
    designation: "",
    department: "",
    bankName: "",
    accountNumber: "",
    ifscCode: "",
    panNumber: "",
    uanNumber: "",
    pfAccountNumber: "",
    esiNumber: "",
    totalWorkingDays: 30,
    paidDays: 30,
    lopDays: 0,
    customFields: [] as CustomField[],
  });

  const [earnings, setEarnings] = useState<SalaryItem[]>(DEFAULT_EARNINGS);
  const [deductions, setDeductions] = useState<SalaryItem[]>(DEFAULT_DEDUCTIONS);
  const [notes, setNotes] = useState<string>(
    "Salary credited via direct bank transfer. For any payroll queries, contact support@kinnowdaily.com."
  );

  // Pre-fill state
  const [staffList, setStaffList] = useState<any[]>([]);
  const [selectedStaffSource, setSelectedStaffSource] = useState<string>("");

  // Preview modal
  const [previewOpen, setPreviewOpen] = useState(false);

  // Load existing staff & global company settings
  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        const settings = await globalSettingsApi.getGlobalSettings();
        if (settings && settings.gstin) {
          const gstinVal = settings.gstin.trim();
          const panVal = gstinVal.length === 15 ? gstinVal.substring(2, 12) : "";
          setCompany((prev) => ({
            ...prev,
            gstin: gstinVal || prev.gstin,
            pan: panVal || prev.pan,
          }));
        }
      } catch (err) {
        console.warn("Could not fetch global settings for company GSTIN:", err);
      }
    };

    const fetchStaff = async () => {
      try {
        const staffData = await adminUsersApi.getUsers();
        if (Array.isArray(staffData)) {
          setStaffList(staffData);
        }
      } catch (err) {
        console.warn("Could not fetch staff for prefill:", err);
      }
    };

    fetchCompanySettings();
    fetchStaff();
  }, []);

  // Handle prefill selection
  const handlePreFillChange = (val: string) => {
    setSelectedStaffSource(val);
    if (!val) return;

    const selected = staffList.find((s) => String(s.id || s._id) === val);
    if (selected) {
      const customFieldsList: CustomField[] = [];
      if (selected.email) {
        customFieldsList.push({ id: "cf-email", label: "Email", value: selected.email });
      }
      if (selected.phone) {
        customFieldsList.push({ id: "cf-phone", label: "Contact Phone", value: selected.phone });
      }
      if (selected.aadharNumber) {
        customFieldsList.push({ id: "cf-aadhar", label: "Aadhar No", value: selected.aadharNumber });
      }

      setEmployee((prev) => ({
        ...prev,
        name: selected.name || selected.username || "",
        employeeId: selected.employeeId || "",
        designation: selected.designation || "",
        department: selected.department || "",
        panNumber: selected.panNumber || "",
        bankName: selected.bankName || "",
        accountNumber: selected.accountNumber || "",
        ifscCode: selected.ifscCode || "",
        uanNumber: selected.uanNumber || "",
        pfAccountNumber: selected.pfAccountNumber || "",
        esiNumber: selected.esiNumber || "",
        customFields: customFieldsList,
      }));
    }
  };

  // Logo upload handler
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCompany((prev) => ({ ...prev, logoUrl: reader.result as string }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Earnings handlers
  const handleAddEarning = () => {
    setEarnings((prev) => [
      ...prev,
      { id: `e-${Date.now()}`, name: "Other Allowance", amount: 0 },
    ]);
  };

  const handleUpdateEarning = (index: number, field: keyof SalaryItem, val: any) => {
    setEarnings((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: val };
      return copy;
    });
  };

  const handleRemoveEarning = (index: number) => {
    setEarnings((prev) => prev.filter((_, i) => i !== index));
  };

  // Deductions handlers
  const handleAddDeduction = () => {
    setDeductions((prev) => [
      ...prev,
      { id: `d-${Date.now()}`, name: "Other Deduction", amount: 0 },
    ]);
  };

  const handleUpdateDeduction = (index: number, field: keyof SalaryItem, val: any) => {
    setDeductions((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: val };
      return copy;
    });
  };

  const handleRemoveDeduction = (index: number) => {
    setDeductions((prev) => prev.filter((_, i) => i !== index));
  };

  // Custom fields handlers
  const handleAddCustomField = () => {
    setEmployee((prev) => ({
      ...prev,
      customFields: [
        ...prev.customFields,
        { id: `cf-${Date.now()}`, label: "Location", value: "Haryana Hub" },
      ],
    }));
  };

  const handleUpdateCustomField = (index: number, field: keyof CustomField, val: string) => {
    setEmployee((prev) => {
      const copy = [...prev.customFields];
      copy[index] = { ...copy[index], [field]: val };
      return { ...prev, customFields: copy };
    });
  };

  const handleRemoveCustomField = (index: number) => {
    setEmployee((prev) => ({
      ...prev,
      customFields: prev.customFields.filter((_, i) => i !== index),
    }));
  };

  // Calculations
  const totalGrossEarnings = earnings.reduce(
    (sum, item) => sum + (Number(item.amount) || 0),
    0
  );
  const totalDeductions = deductions.reduce(
    (sum, item) => sum + (Number(item.amount) || 0),
    0
  );
  const netPay = Math.max(0, totalGrossEarnings - totalDeductions);
  const netPayInWords = numberToIndianWords(netPay);

  // Full Payslip Data
  const payslipData: PayslipData = {
    payMonth,
    payYear,
    company,
    employee,
    earnings,
    deductions,
    notes,
  };

  // Sample data loader
  const handleLoadSample = () => {
    setEmployee({
      name: "Amit Kumar",
      employeeId: "KIN-1092",
      designation: "Senior Kitchen Coordinator",
      department: "Cloud Kitchen Operations",
      bankName: "State Bank of India",
      accountNumber: "30987654321",
      ifscCode: "SBIN0001234",
      panNumber: "BKMPK4920K",
      uanNumber: "101294857291",
      pfAccountNumber: "DL/CPM/84920/19",
      esiNumber: "31009847290001",
      totalWorkingDays: 31,
      paidDays: 31,
      lopDays: 0,
      customFields: [
        { id: "cf-hub", label: "Assigned Hub", value: "Kinnow Central Kitchen" },
        { id: "cf-shift", label: "Shift", value: "Morning (06:00 AM - 03:00 PM)" },
      ],
    });
    setEarnings([
      { id: "e1", name: "Basic Salary", amount: 30000 },
      { id: "e2", name: "House Rent Allowance (HRA)", amount: 12000 },
      { id: "e3", name: "Conveyance Allowance", amount: 4000 },
      { id: "e4", name: "Special Allowance", amount: 6000 },
      { id: "e5", name: "Kitchen Operations Bonus", amount: 3500 },
    ]);
    setDeductions([
      { id: "d1", name: "Provident Fund (EPF)", amount: 2160 },
      { id: "d2", name: "Professional Tax (PT)", amount: 200 },
      { id: "d3", name: "Income Tax (TDS)", amount: 1800 },
    ]);
  };

  // Reset form
  const handleReset = () => {
    setCompany(DEFAULT_COMPANY);
    setEmployee({
      name: "",
      employeeId: "",
      designation: "",
      department: "",
      bankName: "",
      accountNumber: "",
      ifscCode: "",
      panNumber: "",
      uanNumber: "",
      pfAccountNumber: "",
      esiNumber: "",
      totalWorkingDays: 30,
      paidDays: 30,
      lopDays: 0,
      customFields: [],
    });
    setEarnings([
      { id: "e1", name: "Basic Salary", amount: 0 },
      { id: "e2", name: "House Rent Allowance (HRA)", amount: 0 },
      { id: "e3", name: "Special Allowance", amount: 0 },
    ]);
    setDeductions([
      { id: "d1", name: "Provident Fund (EPF)", amount: 0 },
      { id: "d2", name: "Professional Tax (PT)", amount: 0 },
    ]);
    setSelectedStaffSource("");
  };

  return (
    <Layout>
      <Box sx={{ maxWidth: 1200, mx: "auto", pb: 8 }}>
        {/* Page Header */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", md: "center" },
            gap: 2,
            mb: 4,
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                color: "#0f172a",
                letterSpacing: "-0.02em",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <ReceiptIcon sx={{ color: "#f97316", fontSize: 36 }} />
              Payslip Generator
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
              Generate, preview, print, and export official statutory payslips for Kinnow employees, delivery fleet, and staff.
            </Typography>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<AutoAwesomeIcon sx={{ color: "#f97316" }} />}
              onClick={handleLoadSample}
              sx={{ bgcolor: "#ffffff" }}
            >
              Sample Data
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<RestartAltIcon />}
              onClick={handleReset}
              sx={{ bgcolor: "#ffffff" }}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<VisibilityIcon />}
              onClick={() => setPreviewOpen(true)}
              sx={{ px: 3 }}
            >
              Preview & Export
            </Button>
          </Box>
        </Box>

        {/* Quick Pre-fill Ribbon */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: 3,
            bgcolor: "#ffffff",
            border: "1px solid #fed7aa",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <BadgeIcon sx={{ color: "#ea580c" }} />
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#0f172a" }}>
                Auto-fill Staff Details (Optional)
              </Typography>
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                Select a staff member from the directory to pre-fill their details
              </Typography>
            </Box>
          </Box>

          <TextField
            select
            size="small"
            value={selectedStaffSource}
            onChange={(e) => handlePreFillChange(e.target.value)}
            sx={{ minWidth: 280, bgcolor: "#f8fafc" }}
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="">
              <em>-- Select Staff Member to Pre-fill --</em>
            </MenuItem>
            
            {staffList.map((s) => {
              const sId = String(s.id || s._id);
              return (
                <MenuItem key={sId} value={sId}>
                  {s.name || s.username || "Staff Member"}{s.designation ? ` (${s.designation})` : ""}
                </MenuItem>
              );
            })}
          </TextField>
        </Paper>

        <Grid container spacing={3}>
          {/* 1. Company Profile & Header Settings */}
          <Grid size={{ xs: 12 }}>
            <Card sx={{ borderRadius: 3, border: "1px solid #e2e8f0" }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <BusinessIcon sx={{ color: "#f97316" }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a" }}>
                    1. Company & Pay Period Details
                  </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={2.5}>
                  {/* Logo upload */}
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Box
                      sx={{
                        p: 2,
                        border: "2px dashed #cbd5e1",
                        borderRadius: 2,
                        textAlign: "center",
                        bgcolor: "#f8fafc",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                      }}
                    >
                      {company.logoUrl ? (
                        <Box
                          component="img"
                          src={company.logoUrl}
                          alt="Logo"
                          sx={{ maxHeight: 60, maxWidth: "100%", objectFit: "contain" }}
                        />
                      ) : (
                        <Typography variant="caption" sx={{ color: "#64748b" }}>
                          No Logo Uploaded
                        </Typography>
                      )}
                      <Button
                        component="label"
                        size="small"
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        sx={{ fontSize: "0.75rem", mt: 1 }}
                      >
                        Change Logo
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                          onChange={handleLogoUpload}
                        />
                      </Button>
                      {company.logoUrl !== DEFAULT_COMPANY.logoUrl && (
                        <Button
                          size="small"
                          color="inherit"
                          onClick={() =>
                            setCompany((p) => ({ ...p, logoUrl: DEFAULT_COMPANY.logoUrl }))
                          }
                          sx={{ fontSize: "0.7rem", color: "#94a3b8" }}
                        >
                          Reset Logo
                        </Button>
                      )}
                    </Box>
                  </Grid>

                  {/* Company Fields */}
                  <Grid size={{ xs: 12, md: 9 }}>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Company Name"
                          value={company.companyName}
                          onChange={(e) =>
                            setCompany({ ...company, companyName: e.target.value })
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Company Email"
                          value={company.email}
                          onChange={(e) =>
                            setCompany({ ...company, email: e.target.value })
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Registered Address"
                          value={company.addressLine1}
                          onChange={(e) =>
                            setCompany({ ...company, addressLine1: e.target.value })
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="City"
                          value={company.city}
                          onChange={(e) => setCompany({ ...company, city: e.target.value })}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="State"
                          value={company.state}
                          onChange={(e) => setCompany({ ...company, state: e.target.value })}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="PIN Code"
                          value={company.pincode}
                          onChange={(e) =>
                            setCompany({ ...company, pincode: e.target.value })
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="GSTIN (Optional)"
                          value={company.gstin}
                          onChange={(e) => setCompany({ ...company, gstin: e.target.value })}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Company PAN (Optional)"
                          value={company.pan}
                          onChange={(e) => setCompany({ ...company, pan: e.target.value })}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Pay Period info */}
                  <Grid size={{ xs: 12 }}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Payslip Month"
                      value={payMonth}
                      onChange={(e) => setPayMonth(e.target.value)}
                    >
                      {MONTHS.map((m) => (
                        <MenuItem key={m} value={m}>
                          {m}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Year"
                      value={payYear}
                      onChange={(e) => setPayYear(Number(e.target.value))}
                    >
                      {YEARS.map((y) => (
                        <MenuItem key={y} value={y}>
                          {y}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* 2. Employee Details Card */}
          <Grid size={{ xs: 12 }}>
            <Card sx={{ borderRadius: 3, border: "1px solid #e2e8f0" }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <BadgeIcon sx={{ color: "#f97316" }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a" }}>
                      2. Employee Information
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddCustomField}
                  >
                    Add Custom Field
                  </Button>
                </Box>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={2.5}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Employee Name *"
                      value={employee.name}
                      onChange={(e) => setEmployee({ ...employee, name: e.target.value })}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Employee ID *"
                      value={employee.employeeId}
                      onChange={(e) =>
                        setEmployee({ ...employee, employeeId: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Designation"
                      value={employee.designation}
                      onChange={(e) =>
                        setEmployee({ ...employee, designation: e.target.value })
                      }
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Department"
                      value={employee.department}
                      onChange={(e) =>
                        setEmployee({ ...employee, department: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Employee PAN Number"
                      value={employee.panNumber}
                      onChange={(e) =>
                        setEmployee({ ...employee, panNumber: e.target.value })
                      }
                    />
                  </Grid>

                  {/* Banking and Attendance */}
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Bank Name"
                      value={employee.bankName}
                      onChange={(e) =>
                        setEmployee({ ...employee, bankName: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Bank Account No."
                      value={employee.accountNumber}
                      onChange={(e) =>
                        setEmployee({ ...employee, accountNumber: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="IFSC Code"
                      value={employee.ifscCode}
                      onChange={(e) =>
                        setEmployee({ ...employee, ifscCode: e.target.value })
                      }
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="UAN Number"
                      value={employee.uanNumber}
                      onChange={(e) =>
                        setEmployee({ ...employee, uanNumber: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Total Working Days"
                      value={employee.totalWorkingDays}
                      onChange={(e) =>
                        setEmployee({
                          ...employee,
                          totalWorkingDays: Number(e.target.value),
                        })
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Paid Days / Worked"
                      value={employee.paidDays}
                      onChange={(e) =>
                        setEmployee({ ...employee, paidDays: Number(e.target.value) })
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="LOP (Loss of Pay Days)"
                      value={employee.lopDays}
                      onChange={(e) =>
                        setEmployee({ ...employee, lopDays: Number(e.target.value) })
                      }
                    />
                  </Grid>

                  {/* Dynamic Custom Fields */}
                  {employee.customFields.map((cf, idx) => (
                    <Grid size={{ xs: 12, sm: 6 }} key={cf.id}>
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <TextField
                          size="small"
                          label="Field Label"
                          value={cf.label}
                          onChange={(e) =>
                            handleUpdateCustomField(idx, "label", e.target.value)
                          }
                          sx={{ width: "45%" }}
                        />
                        <TextField
                          size="small"
                          label="Value"
                          value={cf.value}
                          onChange={(e) =>
                            handleUpdateCustomField(idx, "value", e.target.value)
                          }
                          sx={{ width: "45%" }}
                        />
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleRemoveCustomField(idx)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* 3. Earnings Table */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ borderRadius: 3, border: "1px solid #e2e8f0", height: "100%" }}>
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PaymentsIcon sx={{ color: "#16a34a" }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a" }}>
                      3. Earnings (Income)
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddEarning}
                  >
                    Add Earning
                  </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {earnings.map((item, idx) => (
                    <Box
                      key={item.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        p: 1,
                        bgcolor: "#f8fafc",
                        borderRadius: 1.5,
                        border: "1px solid #f1f5f9",
                      }}
                    >
                      <TextField
                        size="small"
                        label="Component Name"
                        value={item.name}
                        onChange={(e) =>
                          handleUpdateEarning(idx, "name", e.target.value)
                        }
                        sx={{ flexGrow: 1 }}
                      />
                      <TextField
                        size="small"
                        type="number"
                        label="Amount (₹)"
                        value={item.amount || ""}
                        onChange={(e) =>
                          handleUpdateEarning(idx, "amount", Number(e.target.value))
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">₹</InputAdornment>
                          ),
                        }}
                        sx={{ width: 140 }}
                      />
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleRemoveEarning(idx)}
                        disabled={earnings.length <= 1}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>

                <Paper
                  elevation={0}
                  sx={{
                    mt: 3,
                    p: 2,
                    bgcolor: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography sx={{ fontWeight: 700, color: "#166534" }}>
                    Total Gross Earnings
                  </Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", color: "#15803d" }}>
                    {formatINR(totalGrossEarnings)}
                  </Typography>
                </Paper>
              </CardContent>
            </Card>
          </Grid>

          {/* 4. Deductions Table */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ borderRadius: 3, border: "1px solid #e2e8f0", height: "100%" }}>
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ReceiptIcon sx={{ color: "#dc2626" }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a" }}>
                      4. Deductions
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddDeduction}
                  >
                    Add Deduction
                  </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {deductions.map((item, idx) => (
                    <Box
                      key={item.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        p: 1,
                        bgcolor: "#f8fafc",
                        borderRadius: 1.5,
                        border: "1px solid #f1f5f9",
                      }}
                    >
                      <TextField
                        size="small"
                        label="Deduction Name"
                        value={item.name}
                        onChange={(e) =>
                          handleUpdateDeduction(idx, "name", e.target.value)
                        }
                        sx={{ flexGrow: 1 }}
                      />
                      <TextField
                        size="small"
                        type="number"
                        label="Amount (₹)"
                        value={item.amount || ""}
                        onChange={(e) =>
                          handleUpdateDeduction(idx, "amount", Number(e.target.value))
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">₹</InputAdornment>
                          ),
                        }}
                        sx={{ width: 140 }}
                      />
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleRemoveDeduction(idx)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>

                <Paper
                  elevation={0}
                  sx={{
                    mt: 3,
                    p: 2,
                    bgcolor: "#fef2f2",
                    border: "1px solid #fecaca",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography sx={{ fontWeight: 700, color: "#991b1b" }}>
                    Total Deductions
                  </Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", color: "#b91c1c" }}>
                    {formatINR(totalDeductions)}
                  </Typography>
                </Paper>
              </CardContent>
            </Card>
          </Grid>

          {/* 5. Summary & Net Pay Overview Card */}
          <Grid size={{ xs: 12 }}>
            <Card
              sx={{
                borderRadius: 3,
                border: "2px solid #fed7aa",
                bgcolor: "#fffaf5",
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#c2410c", mb: 2 }}>
                  5. Net Pay & Final Summary
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={3} alignItems="center">
                  <Grid size={{ xs: 12, md: 7 }}>
                    <Box sx={{ mb: 2 }}>
                      <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#475569" }}>
                        Net Salary Calculation:
                      </Typography>
                      <Typography sx={{ fontSize: "1.1rem", color: "#0f172a", mt: 0.5 }}>
                        Gross Earnings ({formatINR(totalGrossEarnings)}) - Total Deductions ({formatINR(totalDeductions)})
                      </Typography>
                    </Box>

                    <Box sx={{ bgcolor: "#ffffff", p: 2, borderRadius: 2, border: "1px solid #fed7aa" }}>
                      <Typography sx={{ fontSize: "0.8rem", color: "#64748b", textTransform: "uppercase" }}>
                        Amount in Words (INR):
                      </Typography>
                      <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: "#0f172a", mt: 0.25 }}>
                        {netPayInWords}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid size={{ xs: 12, md: 5 }}>
                    <Box
                      sx={{
                        p: 3,
                        borderRadius: 3,
                        bgcolor: "#ffffff",
                        border: "2px solid #16a34a",
                        textAlign: "center",
                        boxShadow: "0 4px 15px rgba(22, 163, 74, 0.1)",
                      }}
                    >
                      <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#166534", textTransform: "uppercase" }}>
                        Net Payable Amount
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "2.25rem",
                          fontWeight: 800,
                          color: "#15803d",
                          letterSpacing: "-0.03em",
                          my: 0.5,
                        }}
                      >
                        {formatINR(netPay)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#64748b" }}>
                        Payable for {payMonth} {payYear}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Notes / Remarks */}
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      size="small"
                      label="Notes / Remarks on Payslip"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Floating / Bottom Action Bar */}
        <Box
          sx={{
            mt: 4,
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<VisibilityIcon />}
            onClick={() => setPreviewOpen(true)}
            sx={{ px: 4, py: 1.5, fontSize: "1rem" }}
          >
            Generate & Preview Payslip
          </Button>
        </Box>
      </Box>

      {/* Preview & Export Modal */}
      <PayslipPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        data={payslipData}
      />
    </Layout>
  );
};

export default PayslipGenerator;
